/**
 * Servicio de Conciliación de Instancias de Catálogo Offline.
 *
 * Cuando el técnico agrega activos al catálogo en modo offline, se les asigna una clave
 * provisoria local (ej. "0_3"). Al sincronizarse con el backend, si el servidor asigna
 * un índice diferente (ej. "0_4" porque otro proceso ocupó "0_3"), este servicio
 * reconcilia atómicamente:
 * 1. Las respuestas del formulario en visitaOfflineQueue.
 * 2. Los nombres y referencias de activos en custom_subgroup_names.
 * 3. Los estados de instancias en instance_states.
 * 4. Las operaciones pendientes encoladas posteriores en visitaOfflineOperationQueue.
 * 5. Las referencias de fotos e imágenes encoladas en visitaOfflineMediaQueue.
 * 6. Notifica a la interfaz activa en memoria sin mezclar datos.
 *
 * Gobernanza:
 * - Cero emojis.
 * - Sin dependencias adicionales.
 * - Identificadores camelCase/PascalCase.
 */

import { OFFLINE_STORES, atomicPutBatch, getOfflineStorageDriver } from "./offlineStorageAdapter.js";
import { getVisitaOfflineEntry, generateRevisionUuid } from "./visitaOfflineQueue.js";
import { getVisitaOfflineOperationsByVisita } from "./visitaOfflineOperationQueue.js";
import { getOfflineImageEntriesByVisita } from "./visitaOfflineMediaQueue.js";
import { withVisitaLocalLock } from './visitaLocalFirst.js';

export const VISITA_INSTANCE_RECONCILED_EVENT = "visita:instance-reconciled";

/**
 * Reasigna prefijos de instancia en un mapa de claves (ej. responses).
 *
 * @param {object} sourceMap - Mapa original { "0_3_presion": 10, ... }
 * @param {string} oldPrefix - Clave vieja (ej. "0_3")
 * @param {string} newPrefix - Clave nueva (ej. "0_4")
 * @returns {object} Nuevo mapa con las claves remapeadas
 */
export function remapInstancePrefixInMap(sourceMap, oldPrefix, newPrefix) {
    if (!sourceMap || typeof sourceMap !== "object") {
        return {};
    }

    const remapped = {};
    const exactMatch = String(oldPrefix);
    const prefixMatch = `${oldPrefix}_`;
    const oldStateKey = /^\d+_\d+$/.test(exactMatch) && /^\d+_\d+$/.test(String(newPrefix)) ? `g${exactMatch.replace('_', '_i')}` : null;
    const oldFullStateKey = oldStateKey ? `g${exactMatch.split('_')[0]}_i${exactMatch}` : null;

    for (const [key, value] of Object.entries(sourceMap)) {
        if (oldFullStateKey && key === oldFullStateKey) {
            remapped[`g${String(newPrefix).split('_')[0]}_i${newPrefix}`] = value;
        } else if (oldStateKey && key === oldStateKey) {
            remapped[`g${String(newPrefix).replace('_', '_i')}`] = value;
        } else if (key === exactMatch) {
            remapped[newPrefix] = value;
        } else if (key.startsWith(prefixMatch)) {
            const suffix = key.slice(prefixMatch.length);
            const newKey = `${newPrefix}_${suffix}`;
            remapped[newKey] = value;
        } else {
            remapped[key] = value;
        }
    }

    return remapped;
}

const activeReconciliations = new Map();

export function recordActiveReconciliation(visitaId, oldKey, newKey) {
    const vId = Number(visitaId);
    if (!activeReconciliations.has(vId)) {
        activeReconciliations.set(vId, new Map());
    }
    activeReconciliations.get(vId).set(String(oldKey), String(newKey));
}

export function getActiveReconciliationsForVisita(visitaId) {
    const vId = Number(visitaId);
    return activeReconciliations.get(vId) || new Map();
}

export function sanitizePayloadWithActiveReconciliations(visitaId, payload) {
    if (!payload || typeof payload !== "object") return payload;
    const remappings = getActiveReconciliationsForVisita(visitaId);
    if (remappings.size === 0) return payload;

    let updated = { ...payload };
    for (const [oldKey, newKey] of remappings.entries()) {
        if (updated.responses && typeof updated.responses === "object") {
            updated.responses = remapInstancePrefixInMap(updated.responses, oldKey, newKey);
        }
        if (updated.custom_subgroup_names && typeof updated.custom_subgroup_names === "object") {
            updated.custom_subgroup_names = remapSubgroupNames(updated.custom_subgroup_names, oldKey, newKey);
        }
        if (updated.instance_states && typeof updated.instance_states === "object") {
            updated.instance_states = remapInstancePrefixInMap(updated.instance_states, oldKey, newKey);
        }
    }
    return updated;
}

/**
 * Reasigna los nombres personalizados y referencias de activos en custom_subgroup_names.
 *
 * @param {object} subgroupNames - Mapa de nombres { "0_3": "Hidrante 3", "_activo_id_0_3": 12 }
 * @param {string} oldKey - Clave local vieja (ej. "0_3")
 * @param {string} newKey - Clave del servidor (ej. "0_4")
 * @returns {object}
 */
export function remapSubgroupNames(subgroupNames, oldKey, newKey) {
    if (!subgroupNames || typeof subgroupNames !== "object") {
        return {};
    }

    const remapped = { ...subgroupNames };
    const oldActivoKey = `_activo_id_${oldKey}`;
    const newActivoKey = `_activo_id_${newKey}`;
    const oldGpsKey = `_gps_${oldKey}`;
    const newGpsKey = `_gps_${newKey}`;

    if (Object.prototype.hasOwnProperty.call(remapped, oldKey)) {
        remapped[newKey] = remapped[oldKey];
        delete remapped[oldKey];
    }

    if (Object.prototype.hasOwnProperty.call(remapped, oldActivoKey)) {
        remapped[newActivoKey] = remapped[oldActivoKey];
        delete remapped[oldActivoKey];
    }

    if (Object.prototype.hasOwnProperty.call(remapped, oldGpsKey)) {
        remapped[newGpsKey] = remapped[oldGpsKey];
        delete remapped[oldGpsKey];
    }

    return remapped;
}

/**
 * Ejecuta la conciliación completa entre una instancia local provisoria y la asignada por el servidor.
 *
 * @param {object} params
 * @param {number|string} params.visitaId
 * @param {number|string} [params.userId]
 * @param {string} params.localInstanceKey - Clave local generada offline (ej. "0_3")
 * @param {string} params.serverInstanceKey - Clave definitiva devuelta por el backend (ej. "0_4")
 * @param {object} [params.serverVisita] - Estado opcional devuelto por el servidor
 * @returns {Promise<boolean>} Retorna true si se conciliaron diferencias
 */
export async function reconcileVisitaInstanceKeys({
    visitaId,
    userId = null,
    localInstanceKey,
    serverInstanceKey,
    oldInstanceKey = null,
    newInstanceKey = null,
    serverVisita = null,
    operationUuid = null,
    empresaId = null,
}) {
    return withVisitaLocalLock(userId, visitaId, () => reconcileLocked({ visitaId, userId,
        localInstanceKey, serverInstanceKey, oldInstanceKey, newInstanceKey, serverVisita, operationUuid, empresaId }));
}

async function reconcileLocked({ visitaId, userId, localInstanceKey, serverInstanceKey, oldInstanceKey, newInstanceKey, serverVisita, operationUuid, empresaId }) {
    const rawLocal = localInstanceKey || oldInstanceKey;
    const rawServer = serverInstanceKey || newInstanceKey;
    if (!visitaId || !rawLocal || !rawServer) {
        return false;
    }

    if (String(rawLocal) === String(rawServer)) {
        return false;
    }

    const localKey = String(rawLocal);
    const serverKey = String(rawServer);
    const driver = getOfflineStorageDriver();
    const receiptId = operationUuid ? `instance-remap:${Number(empresaId)}:${Number(userId)}:${Number(visitaId)}:${operationUuid}` : null;
    if (receiptId) {
        if (![empresaId,userId,visitaId].every(value => Number.isSafeInteger(Number(value)) && Number(value)>0)) throw Error('La conciliación requiere una identidad verificable.');
        const previous = await driver.get(OFFLINE_STORES.CONTEXTS, receiptId);
        if (previous) {
            if (previous.kind !== 'instance_reconciliation' || previous.userId !== Number(userId) || previous.empresaId !== Number(empresaId)
                || previous.visitaId !== Number(visitaId) || previous.localKey !== localKey || previous.serverKey !== serverKey) throw Error('El recibo de conciliación no coincide. Datos conservados para revisión.');
            return true; // No volver a mover la clave local: ahora puede pertenecer a otro técnico.
        }
    }
    const operations = await getVisitaOfflineOperationsByVisita(visitaId, userId);
    if (operations.some((op) => op.type === 'add_group' && op.status !== 'applied'
        && op.payload?.local_instance_key === serverKey)) {
        throw new Error('Otra instancia local ocupa la posición asignada por el servidor. Datos conservados para revisión.');
    }

    const mutationBatch = [];
    const nowIso = new Date().toISOString();

    // 1. Conciliar borrador offline en visita_offline_entries
    {
        const offlineEntry = await getVisitaOfflineEntry({ userId, visitaId });
        if (offlineEntry && offlineEntry.payload) {
            const currentPayload = offlineEntry.payload;
            const updatedResponses = remapInstancePrefixInMap(currentPayload.responses, localKey, serverKey);
            const updatedSubgroupNames = remapSubgroupNames(currentPayload.custom_subgroup_names, localKey, serverKey);
            const updatedInstanceStates = remapInstancePrefixInMap(currentPayload.instance_states, localKey, serverKey);

            mutationBatch.push({
                storeName: OFFLINE_STORES.ENTRIES,
                value: {
                    ...offlineEntry,
                    payload: {
                        ...currentPayload,
                        responses: updatedResponses,
                        custom_subgroup_names: updatedSubgroupNames,
                        instance_states: updatedInstanceStates,
                        ...(currentPayload.instance_owners ? {instance_owners: remapInstancePrefixInMap(currentPayload.instance_owners, localKey, serverKey)} : {}),
                        ...(currentPayload._local_first ? { _local_first: { base: {
                            ...currentPayload._local_first.base,
                            responses: remapInstancePrefixInMap(currentPayload._local_first.base.responses, localKey, serverKey),
                            custom_subgroup_names: remapSubgroupNames(currentPayload._local_first.base.custom_subgroup_names, localKey, serverKey),
                            instance_states: remapInstancePrefixInMap(currentPayload._local_first.base.instance_states, localKey, serverKey),
                            ...(currentPayload._local_first.base.instance_owners ? {instance_owners: remapInstancePrefixInMap(currentPayload._local_first.base.instance_owners, localKey, serverKey)} : {}),
                        } } } : {}),
                    },
                    revisionId: generateRevisionUuid(),
                    updatedAt: nowIso,
                },
            });
        }
    }

    // 2. Conciliar operaciones pendientes posteriores encoladas en visita_offline_operations
    {
        if (userId) {
            for (const op of operations) {
                if (op.status === "applied" || op.status === "conflict" || ['add_group','finalizar_visita','iniciar_visita'].includes(op.type)) {
                    continue;
                }

                let modified = false;
                const nextPayload = { ...(op.payload || {}) };

                if (nextPayload.responses && typeof nextPayload.responses === "object") {
                    const remapped = remapInstancePrefixInMap(nextPayload.responses, localKey, serverKey);
                    if (JSON.stringify(remapped) !== JSON.stringify(nextPayload.responses)) {
                        nextPayload.responses = remapped;
                        modified = true;
                    }
                }

                if (nextPayload.custom_subgroup_names && typeof nextPayload.custom_subgroup_names === "object") {
                    const remappedNames = remapSubgroupNames(nextPayload.custom_subgroup_names, localKey, serverKey);
                    if (JSON.stringify(remappedNames) !== JSON.stringify(nextPayload.custom_subgroup_names)) {
                        nextPayload.custom_subgroup_names = remappedNames;
                        modified = true;
                    }
                }

                if (nextPayload.local_instance_key === localKey) {
                    nextPayload.local_instance_key = serverKey;
                    modified = true;
                }

                if (modified) {
                    if (op.inFlightAt || op.retryCount > 0 || op.status === 'in_flight') throw Error('Otra intención ya enviada requiere revisión antes de cambiar su instancia.');
                    mutationBatch.push({
                        storeName: OFFLINE_STORES.OPERATIONS,
                        value: {
                            ...op,
                            payload: nextPayload,
                            updatedAt: nowIso,
                        },
                    });
                }
            }
        }
    }

    // 3. Conciliar imágenes pendientes encoladas en visita_offline_media
    {
        if (userId) {
            const mediaEntries = await getOfflineImageEntriesByVisita(visitaId, userId);
            for (const item of mediaEntries) {
                if (item?.metadata?.instanceRef === localKey) {
                    const file = await driver.get(OFFLINE_STORES.FILES, item.id);
                    if (file?.uploadCommand) throw Error('La foto ya tiene un envío congelado. Asociación conservada para revisión.');
                    mutationBatch.push({
                        storeName: OFFLINE_STORES.MEDIA,
                        value: {
                            ...item,
                            metadata: {
                                ...(item.metadata || {}),
                                instanceRef: serverKey,
                            },
                            updatedAt: nowIso,
                        },
                    });
                }
            }
        }
    }

    // 4. Consolidar atómicamente todo en una sola transacción multi-store
    if (receiptId) mutationBatch.push({storeName:OFFLINE_STORES.CONTEXTS,value:{
        id:receiptId,kind:'instance_reconciliation',userId:Number(userId),empresaId:Number(empresaId),visitaId:Number(visitaId),
        scope:'instance-reconciliation',version:generateRevisionUuid(),templateVersion:'',operationUuid,localKey,serverKey,createdAt:nowIso,
    }});
    if (mutationBatch.length > 0) {
        await atomicPutBatch(mutationBatch);
        recordActiveReconciliation(visitaId, localKey, serverKey);

        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("visita-offline-queue-changed"));
            window.dispatchEvent(new CustomEvent("visita-offline-operation-queue-changed"));
            window.dispatchEvent(new CustomEvent("visita-offline-media-queue-changed"));
        }
    }

    // 5. Notificar a la interfaz activa en memoria
    if (typeof window !== "undefined") {
        window.dispatchEvent(
            new CustomEvent(VISITA_INSTANCE_RECONCILED_EVENT, {
                detail: {
                    visitaId: Number(visitaId),
                    localInstanceKey: localKey,
                    serverInstanceKey: serverKey,
                    serverVisita,
                },
            })
        );
    }

    return true;
}

/**
 * Inspecciona y repara determinísticamente cualquier inconsistencia o guardado parcial
 * (por ejemplo, si el navegador se cerró entre operaciones de versiones anteriores).
 *
 * @param {object} params
 * @param {number|string} params.userId
 * @param {number|string} params.visitaId
 * @param {object} [params.workingVisita] - Estado actual en memoria si existe
 * @returns {Promise<{repaired: boolean, actions: Array<string>, repairedVisita: object|null}>}
 */
export async function repairVisitaOfflineConsistency({
    userId,
    visitaId,
    workingVisita = null,
}) {
    if (!visitaId) {
        return { repaired: false, actions: [], repairedVisita: workingVisita };
    }

    const actions = [];
    let isRepaired = false;

    try {
        const offlineEntry = await getVisitaOfflineEntry({ userId, visitaId });
        const basePayload = offlineEntry?.payload || (workingVisita ? {
            responses: workingVisita.responses || {},
            dynamic_group_items: workingVisita.dynamic_group_items || [],
            custom_subgroup_names: workingVisita.custom_subgroup_names || {},
            instance_states: workingVisita.instance_states || {},
        } : null);

        if (!basePayload) {
            return { repaired: false, actions: [], repairedVisita: workingVisita };
        }

        const currentDynamicItems = Array.isArray(basePayload.dynamic_group_items)
            ? JSON.parse(JSON.stringify(basePayload.dynamic_group_items))
            : [];
        const currentSubgroupNames = basePayload.custom_subgroup_names && typeof basePayload.custom_subgroup_names === "object"
            ? { ...basePayload.custom_subgroup_names }
            : {};

        const operations = userId ? await getVisitaOfflineOperationsByVisita(visitaId, userId) : [];
        if (operations.some((op) => op.type === 'remove_group' && op.status !== 'applied')) {
            return { repaired: false, actions: [], repairedVisita: workingVisita };
        }
        const addGroupOps = operations.filter((op) => op.type === "add_group" && op.status === "pending");

        for (const op of addGroupOps) {
            const localKey = op?.payload?.local_instance_key;
            const groupIdx = op?.payload?.group_idx;
            const instanceName = op?.payload?.instance_name;

            if (localKey && !currentSubgroupNames[localKey]) {
                currentSubgroupNames[localKey] = instanceName || `Activo N° ${localKey}`;
                if (op?.payload?.activo_id) {
                    currentSubgroupNames[`_activo_id_${localKey}`] = Number(op.payload.activo_id);
                }

                isRepaired = true;
                actions.push(`Reparado activo huérfano ${localKey} desde operación outbox ${op.id}`);
            }
        }

        // Un nombre ausente no prueba que falte una instancia. Las altas nuevas
        // guardan el contador y la outbox atómicamente, incluso sin nombre manual.
        const baseline = basePayload._local_first?.base?.dynamic_group_items;
        if (Array.isArray(baseline)) {
            const pending = new Map();
            for (const op of addGroupOps) {
                const group = String(op.payload?.group_idx ?? ''), key = op.payload?.local_instance_key;
                if (!group || !key) continue;
                if (!pending.has(group)) pending.set(group, new Set());
                pending.get(group).add(key);
            }
            for (const [group, keys] of pending) {
                const base = Number(baseline.find(item => String(item.group_parent) === group)?.additional_count || 0);
                const expected = base + keys.size, item = currentDynamicItems.find(value => String(value.group_parent) === group);
                if (Number(item?.additional_count || 0) >= expected) continue;
                if (item) item.additional_count = expected;
                else currentDynamicItems.push({ group_parent: group, additional_count: expected });
                isRepaired = true;
                actions.push(`Restauradas las instancias pendientes del grupo ${group}`);
            }
        }

        if (isRepaired) {
            const repairedPayload = {
                ...basePayload,
                dynamic_group_items: currentDynamicItems,
                custom_subgroup_names: currentSubgroupNames,
            };

            const nowIso = new Date().toISOString();
            const nextEntry = {
                ...(offlineEntry || {
                    key: `${userId || "anon"}:${visitaId}`,
                    userId: Number(userId) || null,
                    visitaId: Number(visitaId),
                }),
                payload: repairedPayload,
                revisionId: generateRevisionUuid(),
                updatedAt: nowIso,
            };
            const saved = await getOfflineStorageDriver().mutate(OFFLINE_STORES.ENTRIES, nextEntry.key, current =>
                (offlineEntry ? current?.revisionId === offlineEntry.revisionId && JSON.stringify(current.payload) === JSON.stringify(basePayload) : !current)
                    ? nextEntry : null);
            if (!saved) return { repaired: false, actions: ['La copia local cambió durante la comprobación; se conserva para reintentar.'], repairedVisita: workingVisita };

            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("visita-offline-queue-changed"));
            }

            const repairedVisita = workingVisita ? {
                ...workingVisita,
                dynamic_group_items: currentDynamicItems,
                custom_subgroup_names: currentSubgroupNames,
            } : null;

            return { repaired: true, actions, repairedVisita };
        }
    } catch (err) {
        actions.push(`Error durante chequeo de auto-reparación: ${err?.message || String(err)}`);
    }

    return { repaired: false, actions, repairedVisita: workingVisita };
}
